import { gql } from "@apollo/client";
import type {
  ContentSourceInput,
  ConversationAiConfigInput,
  AiSettingsInput,
  CalendlyIntegrationInput,
} from "../../generated/graphql";

// Conversation Fragments
export const CONVERSATION_FRAGMENT = gql`
  fragment ConversationFragment on Conversation {
    id
    tenantId
    name
    description
    status
    startDate
    endDate
    targetAudience {
      segments
      criteria
      estimatedReach
    }
    assets {
      id
      type
      fileId
      trackingEnabled
      qrCodeEnabled
    }
    startMessages {
      id
      messageId
      name
      content
      type
      isActive
    }
    destinations {
      id
      destinationId
      name
      url
      type
      isActive
    }
    autoResponderId
    aiConfig {
      aiChatEnabled
      contentSource {
        type
        destinationId
        fileId
        analysisStatus
      }
      calendlyIntegration {
        enabled
        calendarLink
        connectionStatus
        eventTypeId
        bookingSettings {
          autoConfirm
          requirePayment
          customFields
        }
      }
      aiSettings {
        model
        temperature
        maxTokens
        systemPrompt
      }
    }
    createdAt
    updatedAt
    version
  }
`;

export const CONVERSATION_WITH_AUTO_RESPONDER_FRAGMENT = gql`
  fragment ConversationWithAutoResponderFragment on Conversation {
    ...ConversationFragment
    autoResponder {
      id
      name
      content
      isActive
      tags
      deviceActions {
        mobileAction
        desktopAction
      }
    }
  }
  ${CONVERSATION_FRAGMENT}
`;

// Queries
export const GET_CONVERSATION = gql`
  query GetConversation($id: ID!) {
    getConversation(id: $id) {
      ...ConversationWithAutoResponderFragment
    }
  }
  ${CONVERSATION_WITH_AUTO_RESPONDER_FRAGMENT}
`;

export const LIST_CONVERSATIONS = gql`
  query ListConversations(
    $filter: ConversationFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listConversations(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        ...ConversationWithAutoResponderFragment
      }
      nextToken
      total
    }
  }
  ${CONVERSATION_WITH_AUTO_RESPONDER_FRAGMENT}
`;

// Mutations
export const CREATE_CONVERSATION = gql`
  mutation CreateConversation($input: CreateConversationInput!) {
    createConversation(input: $input) {
      ...ConversationWithAutoResponderFragment
    }
  }
  ${CONVERSATION_WITH_AUTO_RESPONDER_FRAGMENT}
`;

export const UPDATE_CONVERSATION = gql`
  mutation UpdateConversation($input: UpdateConversationInput!) {
    updateConversation(input: $input) {
      ...ConversationWithAutoResponderFragment
    }
  }
  ${CONVERSATION_WITH_AUTO_RESPONDER_FRAGMENT}
`;

export const DELETE_CONVERSATION = gql`
  mutation DeleteConversation($id: ID!) {
    deleteConversation(id: $id)
  }
`;

export const ARCHIVE_CONVERSATION = gql`
  mutation ArchiveConversation($id: ID!) {
    archiveConversation(id: $id) {
      ...ConversationFragment
    }
  }
  ${CONVERSATION_FRAGMENT}
`;

export const UNARCHIVE_CONVERSATION = gql`
  mutation UnarchiveConversation($id: ID!) {
    unarchiveConversation(id: $id) {
      ...ConversationFragment
    }
  }
  ${CONVERSATION_FRAGMENT}
`;

// Auto-Responder Attachment Operations are now in auto-responder-operations.ts

// Asset Attachment Operations
export const ATTACH_ASSETS_TO_CONVERSATION = gql`
  mutation AttachAssetsToConversation($conversationId: ID!, $assetIds: [ID!]!) {
    attachAssetsToConversation(
      conversationId: $conversationId
      assetIds: $assetIds
    ) {
      ...ConversationWithAutoResponderFragment
    }
  }
  ${CONVERSATION_WITH_AUTO_RESPONDER_FRAGMENT}
`;

// TypeScript types for the operations
export interface ConversationVariables {
  id: string;
}

export interface ListConversationsVariables {
  filter?: {
    status?: string;
    autoResponderId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  };
  limit?: number;
  nextToken?: string;
}

export interface CreateConversationVariables {
  input: {
    name: string;
    description?: string;
    status?: string;
    startDate: string;
    endDate?: string;
    targetAudience: {
      segments: string[];
      criteria: Record<string, any>;
      estimatedReach?: number;
    };
    assets?: Array<{
      id: string;
      type: string;
      fileId: string;
      trackingEnabled: boolean;
      qrCodeEnabled: boolean;
    }>;
    startMessages?: Array<{
      id: string;
      messageId: string;
      name: string;
      content: string;
      type: string;
      isActive: boolean;
    }>;
    destinations?: Array<{
      id: string;
      destinationId: string;
      name: string;
      url: string;
      type: string;
      isActive: boolean;
    }>;
    autoResponderId?: string;
    aiConfig?: ConversationAiConfigInput;
  };
}

export interface UpdateConversationVariables {
  input: {
    id: string;
    name?: string;
    description?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    targetAudience?: {
      segments: string[];
      criteria: Record<string, any>;
      estimatedReach?: number;
    };
    assets?: Array<{
      id: string;
      type: string;
      fileId: string;
      trackingEnabled: boolean;
      qrCodeEnabled: boolean;
    }>;
    autoResponderId?: string;
    aiConfig?: ConversationAiConfigInput;
  };
}

export interface DeleteConversationVariables {
  id: string;
}

export interface AttachAutoResponderVariables {
  autoResponderId: string;
  conversationId: string;
}

export interface DetachAutoResponderVariables {
  autoResponderId: string;
  conversationId: string;
}

export interface AttachAssetsToConversationVariables {
  conversationId: string;
  assetIds: string[];
}

// Response types
export interface Conversation {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  status: string;
  startDate: string;
  endDate?: string;
  targetAudience: {
    segments: string[];
    criteria: Record<string, any>;
    estimatedReach?: number;
  };
  assets: Array<{
    id: string;
    type: string;
    fileId: string;
    trackingEnabled: boolean;
    qrCodeEnabled: boolean;
  }>;
  startMessages: Array<{
    id: string;
    messageId: string;
    name: string;
    content: string;
    type: string;
    isActive: boolean;
  }>;
  destinations: Array<{
    id: string;
    destinationId: string;
    name: string;
    url: string;
    type: string;
    isActive: boolean;
  }>;
  autoResponderId?: string;
  autoResponder?: {
    id: string;
    name: string;
    content: string;
    isActive: boolean;
    tags: string[];
    deviceActions: {
      mobileAction: string;
      desktopAction: string;
    };
  };
  aiConfig?: ConversationAiConfigInput;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface ConversationConnection {
  items: Conversation[];
  nextToken?: string;
  total: number;
}

export interface GetConversationResponse {
  getConversation: Conversation | null;
}

export interface ListConversationsResponse {
  listConversations: ConversationConnection;
}

export interface CreateConversationResponse {
  createConversation: Conversation;
}

export interface UpdateConversationResponse {
  updateConversation: Conversation;
}

export interface DeleteConversationResponse {
  deleteConversation: boolean;
}

export interface ArchiveConversationVariables {
  id: string;
}

export interface ArchiveConversationResponse {
  archiveConversation: Conversation;
}

export interface UnarchiveConversationVariables {
  id: string;
}

export interface UnarchiveConversationResponse {
  unarchiveConversation: Conversation;
}

// Auto-Responder response interfaces are now in auto-responder-operations.ts

export interface AttachAssetsToConversationResponse {
  attachAssetsToConversation: Conversation;
}
